/**
 * SPEC-008 §49/§50/§52/plan §17 — cycles are structurally allowed
 * (SPEC-006) but V1 does not solve recursive/iterative metric systems.
 * This module only detects which nodes participate in a cyclic dependency
 * (a strongly connected component of size > 1) so the propagation engine
 * can isolate them and require a manual anchor instead of looping forever.
 *
 * Tarjan's algorithm, iterative (no recursion) so a large/pathological
 * graph cannot blow the call stack — the propagation engine must never
 * hang or recurse indefinitely (METRIC-REQ-018).
 */
export interface DependencyEdge {
  from: string;
  to: string;
}

export function findStronglyConnectedComponents(
  nodeIds: readonly string[],
  edges: readonly DependencyEdge[],
): string[][] {
  const adjacency = new Map<string, string[]>();
  for (const id of nodeIds) adjacency.set(id, []);
  for (const edge of edges) {
    if (!adjacency.has(edge.from)) continue;
    adjacency.get(edge.from)!.push(edge.to);
  }

  const index = new Map<string, number>();
  const lowlink = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const components: string[][] = [];
  let counter = 0;

  type Frame = { node: string; neighborIndex: number };

  for (const start of nodeIds) {
    if (index.has(start)) continue;

    const callStack: Frame[] = [{ node: start, neighborIndex: 0 }];
    index.set(start, counter);
    lowlink.set(start, counter);
    counter += 1;
    stack.push(start);
    onStack.add(start);

    while (callStack.length > 0) {
      const frame = callStack[callStack.length - 1]!;
      const neighbors = adjacency.get(frame.node) ?? [];

      if (frame.neighborIndex < neighbors.length) {
        const next = neighbors[frame.neighborIndex]!;
        frame.neighborIndex += 1;

        if (!index.has(next)) {
          index.set(next, counter);
          lowlink.set(next, counter);
          counter += 1;
          stack.push(next);
          onStack.add(next);
          callStack.push({ node: next, neighborIndex: 0 });
        } else if (onStack.has(next)) {
          lowlink.set(frame.node, Math.min(lowlink.get(frame.node)!, index.get(next)!));
        }
        continue;
      }

      // All neighbors visited: pop this frame, propagate lowlink to parent.
      callStack.pop();
      if (callStack.length > 0) {
        const parent = callStack[callStack.length - 1]!;
        lowlink.set(parent.node, Math.min(lowlink.get(parent.node)!, lowlink.get(frame.node)!));
      }

      if (lowlink.get(frame.node) === index.get(frame.node)) {
        const component: string[] = [];
        let member: string;
        do {
          member = stack.pop()!;
          onStack.delete(member);
          component.push(member);
        } while (member !== frame.node);
        components.push(component);
      }
    }
  }

  return components;
}

/** A component of size 1 with no self-loop is not a cycle; self-loops are
 * already blocked at the edge-validation layer (SPEC-006), so size > 1 is
 * the only cyclic case this engine needs to isolate. */
export function cyclicNodeIds(nodeIds: readonly string[], edges: readonly DependencyEdge[]): Set<string> {
  const components = findStronglyConnectedComponents(nodeIds, edges);
  const cyclic = new Set<string>();
  for (const component of components) {
    if (component.length > 1) {
      for (const id of component) cyclic.add(id);
    }
  }
  return cyclic;
}
